import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, RefreshCw, DollarSign, Users, CreditCard, TrendingUp, Gift, AlertTriangle, Mail, X, UserPlus, Trash2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabase/supabaseClient';

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';

interface Purchase {
  id: string;
  user_id: string;
  credits_total: number;
  price_vnd: number;
  payment_method: string;
  payment_status: string;
  payment_reference: string | null;
  transfer_code?: string | null;
  created_at: string;
  profiles?: { email?: string; display_name?: string };
}

interface UserBalance {
  user_id: string;
  email?: string;
  credits: number;
  credits_used: number;
  credits_purchased: number;
  updated_at: string;
  created_at?: string;
  last_sign_in_at?: string | null;
  confirmed?: boolean;
  deleted_at?: string | null;
  profiles?: { display_name?: string; email?: string; phone?: string; phone_verified?: boolean; role?: string; organization?: string };
}

interface CreditTransaction {
  id: string;
  user_id: string;
  type: string;
  credits: number;
  balance_after: number;
  reason: string | null;
  created_at: string;
}

interface PaymentException {
  id: string;
  exception_type: 'amount_overpaid' | 'amount_too_small' | 'unknown_dtl' | 'expired_order_paid' | 'cancelled_order_paid' | 'parse_error' | 'duplicate_confirmed';
  parsed_amount: number | null;
  parsed_transfer_code: string | null;
  matched_purchase_id: string | null;
  status: 'unresolved' | 'resolved' | 'ignored';
  resolution_action: string | null;
  resolution_note: string | null;
  created_at: string;
  bank_email?: { email_subject?: string; email_body?: string; email_from?: string; received_at?: string };
  purchase?: { id: string; price_vnd: number; credits_total: number; user_id: string; transfer_code: string; payment_status: string };
}

interface BankEmailRow {
  id: string;
  msg_id: string;
  email_from: string;
  email_subject: string;
  parsed_amount: number | null;
  parsed_transfer_code: string | null;
  processing_status: 'received' | 'confirmed' | 'exception' | 'rejected_sender' | 'ignored_outgoing';
  matched_purchase_id: string | null;
  received_at: string;
}

type Tab = 'overview' | 'purchases' | 'exceptions' | 'mail_log' | 'users' | 'transactions';

const CommercialAdmin: React.FC = () => {
  const { session } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [users, setUsers] = useState<UserBalance[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [exceptions, setExceptions] = useState<PaymentException[]>([]);
  const [bankEmails, setBankEmails] = useState<BankEmailRow[]>([]);
  const [exceptionFilter, setExceptionFilter] = useState<'unresolved' | 'resolved' | 'ignored'>('unresolved');
  const [activeException, setActiveException] = useState<PaymentException | null>(null);
  const [viewedExceptionIds, setViewedExceptionIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('admin_viewed_exceptions');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const markExceptionViewed = useCallback((id: string) => {
    setViewedExceptionIds(prev => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem('admin_viewed_exceptions', JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);
  const [resolveNote, setResolveNote] = useState('');
  const [resolveCreditsAmount, setResolveCreditsAmount] = useState('');
  const [resolving, setResolving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [bonusUserId, setBonusUserId] = useState('');
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusReason, setBonusReason] = useState('');
  const [bonusSending, setBonusSending] = useState(false);
  const [userActionLoading, setUserActionLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [purchasesRes, txRes] = await Promise.all([
        supabase.from('purchases').select('*, profiles(display_name, email)').order('created_at', { ascending: false }).limit(100),
        supabase.from('credit_transactions').select('*').order('created_at', { ascending: false }).limit(200),
      ]);
      if (purchasesRes.data) setPurchases(purchasesRes.data as any);
      if (txRes.data) setTransactions(txRes.data as any);
      if (session?.access_token) {
        const usersRes = await fetch(`${WORKERS_URL}/api/admin/users?per_page=200`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (usersRes.ok) {
          const data = await usersRes.json() as { users?: UserBalance[] };
          setUsers(data.users || []);
        }
      }
    } catch (e) {
      console.error('Fetch error:', e);
    }
    setLoading(false);
  }, [session?.access_token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto refresh every 15s
  useEffect(() => {
    const interval = setInterval(fetchAll, 15_000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  // Fetch exceptions + bank emails khi vào tab
  const fetchExceptions = useCallback(async (status: 'unresolved' | 'resolved' | 'ignored') => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/exceptions?status=${status}&limit=50`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const { exceptions: rows } = await res.json();
        setExceptions(rows);
      }
    } catch (e) { console.error('Fetch exceptions:', e); }
  }, [session]);

  const fetchBankEmails = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/bank-emails?limit=100`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        const { emails } = await res.json();
        setBankEmails(emails);
      }
    } catch (e) { console.error('Fetch bank emails:', e); }
  }, [session]);

  useEffect(() => {
    if (tab === 'exceptions') fetchExceptions(exceptionFilter);
    if (tab === 'mail_log') fetchBankEmails();
  }, [tab, exceptionFilter, fetchExceptions, fetchBankEmails]);

  const resolveException = async (action: ResolutionAction, purchaseIdOverride?: string) => {
    if (!activeException || !session?.access_token) return;
    setResolving(true);
    try {
      const body: any = { action, note: resolveNote || undefined };
      if ((action === 'credits_added_manual' || action === 'manual_credit_after_verification') && resolveCreditsAmount) {
        body.creditsAmount = parseInt(resolveCreditsAmount, 10);
      }
      if (purchaseIdOverride) body.purchaseIdOverride = purchaseIdOverride;
      const res = await fetch(`${WORKERS_URL}/api/admin/exceptions/${activeException.id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setMessage({ type: 'ok', text: `Resolved: ${action}` });
        setActiveException(null);
        setResolveNote('');
        setResolveCreditsAmount('');
        fetchExceptions(exceptionFilter);
        fetchAll();
      } else {
        const e = await res.json().catch(() => ({}));
        setMessage({ type: 'err', text: e.error || 'Resolve failed' });
      }
    } catch (e: any) {
      setMessage({ type: 'err', text: e.message });
    } finally {
      setResolving(false);
    }
  };

  const confirmPayment = async (purchaseId: string, ref?: string) => {
    if (!session?.access_token) return;
    setConfirming(purchaseId);
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/confirm-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ purchaseId, paymentReference: ref || `ADMIN-${Date.now()}` }),
      });
      const data = await res.json() as any;
      if (res.ok) {
        setMessage({ type: 'ok', text: `Da cong ${data.credits_added?.toLocaleString()} credits. Balance moi: ${data.new_balance?.toLocaleString()}` });
        fetchAll();
      } else {
        setMessage({ type: 'err', text: data.error || 'Loi confirm' });
      }
    } catch (e: any) {
      setMessage({ type: 'err', text: e.message });
    }
    setConfirming(null);
  };

  const addBonus = async () => {
    if (!session?.access_token || !bonusUserId || !bonusAmount) return;
    setBonusSending(true);
    try {
      // US-19-12 Tier 1: route via Worker /api/admin/add-credits (add_credits RPC
      // EXECUTE revoked from authenticated → service_role only via Worker proxy).
      const res = await fetch(`${WORKERS_URL}/api/admin/add-credits`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: bonusUserId,
          credits: parseInt(bonusAmount),
          type: 'bonus',
          reason: bonusReason || 'Admin bonus',
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setMessage({ type: 'ok', text: `Da cong ${parseInt(bonusAmount).toLocaleString()} bonus credits. Balance: ${json.new_balance}` });
      setBonusUserId('');
      setBonusAmount('');
      setBonusReason('');
      fetchAll();
    } catch (e: any) {
      setMessage({ type: 'err', text: e.message });
    }
    setBonusSending(false);
  };

  const createAdminUsers = async (payload: any) => {
    if (!session?.access_token) return;
    setUserActionLoading(true);
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({})) as any;
      if (!res.ok && res.status !== 207) throw new Error(data.error || `HTTP ${res.status}`);
      const ok = data.ok ?? 0;
      const failed = data.failed ?? 0;
      setMessage({
        type: failed ? 'err' : 'ok',
        text: failed ? `Tao ${ok} user thanh cong, ${failed} loi` : `Da tao ${ok} user`,
      });
      fetchAll();
      return data;
    } catch (e: any) {
      setMessage({ type: 'err', text: e.message });
      throw e;
    } finally {
      setUserActionLoading(false);
    }
  };

  const deleteAdminUser = async (userId: string, email?: string) => {
    if (!session?.access_token) return;
    const ok = window.confirm(`Soft-delete tai khoan ${email || userId}? User se khong dang nhap duoc, du lieu/audit duoc giu lai.`);
    if (!ok) return;
    setUserActionLoading(true);
    try {
      const res = await fetch(`${WORKERS_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json().catch(() => ({})) as any;
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setMessage({ type: 'ok', text: `Da soft-delete ${email || userId}` });
      fetchAll();
    } catch (e: any) {
      setMessage({ type: 'err', text: e.message });
    } finally {
      setUserActionLoading(false);
    }
  };

  // Stats
  const pendingPurchases = purchases.filter(p => p.payment_status === 'pending');
  const paidPurchases = purchases.filter(p => p.payment_status === 'paid');
  const totalRevenue = paidPurchases.reduce((sum, p) => sum + Number(p.price_vnd), 0);
  const totalCreditsIssued = paidPurchases.reduce((sum, p) => sum + p.credits_total, 0);
  const totalUsers = users.length;
  const verifiedUsers = users.filter(u => u.profiles?.phone_verified).length;
  const activeUsers = users.filter(u => Number(u.credits_used) > 0).length;

  return (
    <div className="h-full flex flex-col bg-slate-100 font-sans text-slate-800 overflow-hidden">
      {/* Message bar */}
      {message && (
        <div className={`px-4 py-2 text-sm font-medium flex items-center gap-2 shrink-0 ${message.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'ok' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto text-xs underline">Dong</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-6 px-6 pt-4 border-b border-slate-200 shrink-0 bg-white">
        {([
          ['overview', 'Tong Quan'],
          ['purchases', `Don Hang ${pendingPurchases.length > 0 ? `(${pendingPurchases.length})` : ''}`],
          ['exceptions', (() => {
            const unresolvedCount = exceptions.filter((e: PaymentException) => e.status === 'unresolved').length;
            const unviewedCount = exceptions.filter((e: PaymentException) => e.status === 'unresolved' && !viewedExceptionIds.has(e.id)).length;
            if (unviewedCount > 0) return `Ngoai Le 🔴 ${unviewedCount}/${unresolvedCount}`;
            if (unresolvedCount > 0) return `Ngoai Le (${unresolvedCount})`;
            return 'Ngoai Le';
          })()],
          ['mail_log', 'Mail Log'],
          ['users', 'Users'],
          ['transactions', 'Giao Dich'],
        ] as [Tab, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`pb-3 text-[11px] uppercase font-black tracking-widest transition-all relative
              ${tab === id ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}>
            {label}
            {tab === id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full" />}
          </button>
        ))}
        <button onClick={fetchAll} className="ml-auto pb-3 text-slate-400 hover:text-indigo-600 transition-all">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {tab === 'overview' && <OverviewTab
          pendingCount={pendingPurchases.length}
          totalRevenue={totalRevenue}
          totalCreditsIssued={totalCreditsIssued}
          totalUsers={totalUsers}
          verifiedUsers={verifiedUsers}
          activeUsers={activeUsers}
          recentPurchases={purchases.slice(0, 5)}
          onConfirm={confirmPayment}
          confirming={confirming}
          bonusUserId={bonusUserId}
          setBonusUserId={setBonusUserId}
          bonusAmount={bonusAmount}
          setBonusAmount={setBonusAmount}
          bonusReason={bonusReason}
          setBonusReason={setBonusReason}
          bonusSending={bonusSending}
          onAddBonus={addBonus}
        />}
        {tab === 'purchases' && <PurchasesTab purchases={purchases} onConfirm={confirmPayment} confirming={confirming} />}
        {tab === 'exceptions' && (
          <ExceptionsTab
            exceptions={exceptions}
            filter={exceptionFilter}
            onFilterChange={setExceptionFilter}
            viewedIds={viewedExceptionIds}
            onSelect={(ex) => {
              markExceptionViewed(ex.id);
              setActiveException(ex);
              setResolveCreditsAmount(ex.purchase?.credits_total ? String(ex.purchase.credits_total) : '');
              setResolveNote('');
            }}
          />
        )}
        {tab === 'mail_log' && <MailLogTab emails={bankEmails} />}
        {tab === 'users' && (
          <UsersTab
            users={users}
            actionLoading={userActionLoading}
            onCreateUsers={createAdminUsers}
            onDeleteUser={deleteAdminUser}
          />
        )}
        {tab === 'transactions' && <TransactionsTab transactions={transactions} />}
      </div>

      {/* Drawer chi tiết exception */}
      {activeException && (
        <ExceptionDrawer
          exception={activeException}
          note={resolveNote}
          setNote={setResolveNote}
          creditsAmount={resolveCreditsAmount}
          setCreditsAmount={setResolveCreditsAmount}
          resolving={resolving}
          onResolve={resolveException}
          onClose={() => setActiveException(null)}
          accessToken={session?.access_token}
        />
      )}
    </div>
  );
};

// ==========================================
// OVERVIEW TAB
// ==========================================
function OverviewTab({ pendingCount, totalRevenue, totalCreditsIssued, totalUsers, verifiedUsers, activeUsers,
  recentPurchases, onConfirm, confirming,
  bonusUserId, setBonusUserId, bonusAmount, setBonusAmount, bonusReason, setBonusReason, bonusSending, onAddBonus,
}: any) {
  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4">
        <KpiCard icon={CreditCard} label="Don Cho Duyet" value={pendingCount} color={pendingCount > 0 ? 'text-red-600' : 'text-emerald-600'} bg={pendingCount > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'} />
        <KpiCard icon={DollarSign} label="Doanh Thu" value={`${(totalRevenue / 1000).toLocaleString()}K`} sub="VND" color="text-violet-700" bg="bg-violet-50 border-violet-200" />
        <KpiCard icon={Users} label="Users" value={totalUsers} sub={`${verifiedUsers} verified, ${activeUsers} active`} color="text-blue-700" bg="bg-blue-50 border-blue-200" />
        <KpiCard icon={TrendingUp} label="Credits Ban Ra" value={`${(totalCreditsIssued / 1_000_000).toFixed(1)}M`} color="text-amber-700" bg="bg-amber-50 border-amber-200" />
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Pending purchases (quick action) */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mb-3">Don Hang Gan Day</h3>
          {recentPurchases.length === 0 ? (
            <div className="text-slate-400 text-sm italic py-4 text-center">Chua co don hang</div>
          ) : (
            <div className="space-y-2">
              {recentPurchases.map((p: Purchase) => (
                <div key={p.id} className={`flex items-center justify-between p-2 rounded text-xs border ${p.payment_status === 'pending' ? 'border-amber-200 bg-amber-50' : 'border-slate-100 bg-slate-50'}`}>
                  <div>
                    <span className="font-medium">{p.profiles?.display_name || p.user_id.slice(0, 8)}</span>
                    <span className="text-slate-400 ml-2">{Number(p.price_vnd).toLocaleString()}d</span>
                    <span className="text-slate-400 ml-2">{p.payment_method}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={p.payment_status} />
                    {p.payment_status === 'pending' && (
                      <button onClick={() => onConfirm(p.id)} disabled={confirming === p.id}
                        className="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold disabled:opacity-50">
                        {confirming === p.id ? '...' : 'Duyet'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick bonus */}
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h3 className="text-[10px] uppercase font-bold text-slate-400 tracking-widest mb-3 flex items-center gap-1">
            <Gift className="w-3 h-3" /> Cong Bonus Credits
          </h3>
          <div className="space-y-2">
            <input type="text" placeholder="User ID (UUID)" value={bonusUserId} onChange={e => setBonusUserId(e.target.value)}
              className="w-full px-3 py-1.5 border border-slate-200 rounded text-xs font-mono focus:border-indigo-400 focus:outline-none" />
            <input type="number" placeholder="So credits" value={bonusAmount} onChange={e => setBonusAmount(e.target.value)}
              className="w-full px-3 py-1.5 border border-slate-200 rounded text-xs font-mono focus:border-indigo-400 focus:outline-none" />
            <input type="text" placeholder="Ly do (optional)" value={bonusReason} onChange={e => setBonusReason(e.target.value)}
              className="w-full px-3 py-1.5 border border-slate-200 rounded text-xs focus:border-indigo-400 focus:outline-none" />
            <button onClick={onAddBonus} disabled={bonusSending || !bonusUserId || !bonusAmount}
              className="w-full py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-bold disabled:opacity-50 transition-all">
              {bonusSending ? 'Dang cong...' : 'Cong Credits'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// PURCHASES TAB
// ==========================================
function PurchasesTab({ purchases, onConfirm, confirming }: { purchases: Purchase[]; onConfirm: any; confirming: string | null }) {
  const [filter, setFilter] = useState<string>('all');
  const filtered = filter === 'all' ? purchases : purchases.filter(p => p.payment_status === filter);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {['all', 'pending', 'paid', 'failed'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded text-[10px] uppercase font-bold transition-all
              ${filter === f ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
            {f} {f !== 'all' && `(${purchases.filter(p => p.payment_status === f).length})`}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left p-3 text-[10px] uppercase font-bold text-slate-400">Thoi Gian</th>
              <th className="text-left p-3 text-[10px] uppercase font-bold text-slate-400">User</th>
              <th className="text-right p-3 text-[10px] uppercase font-bold text-slate-400">So Tien</th>
              <th className="text-right p-3 text-[10px] uppercase font-bold text-slate-400">Credits</th>
              <th className="text-center p-3 text-[10px] uppercase font-bold text-slate-400">Phuong Thuc</th>
              <th className="text-center p-3 text-[10px] uppercase font-bold text-slate-400">Ma CK</th>
              <th className="text-center p-3 text-[10px] uppercase font-bold text-slate-400">Trang Thai</th>
              <th className="text-center p-3 text-[10px] uppercase font-bold text-slate-400">Hanh Dong</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map(p => (
              <tr key={p.id} className={`hover:bg-slate-50 ${p.payment_status === 'pending' ? 'bg-amber-50/50' : ''}`}>
                <td className="p-3 font-mono text-slate-500">{new Date(p.created_at).toLocaleString('vi-VN')}</td>
                <td className="p-3">
                  <div className="font-medium">{p.profiles?.display_name || '—'}</div>
                  <div className="text-[10px] text-slate-400">{p.profiles?.email || p.user_id.slice(0, 12)}</div>
                </td>
                <td className="p-3 text-right font-mono font-bold">{Number(p.price_vnd).toLocaleString()}d</td>
                <td className="p-3 text-right font-mono">{p.credits_total.toLocaleString()}</td>
                  <td className="p-3 text-center"><MethodBadge method={p.payment_method} /></td>
                <td className="p-3 text-center"><span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{p.transfer_code || '—'}</span></td>
                <td className="p-3 text-center"><StatusBadge status={p.payment_status} /></td>
                <td className="p-3 text-center">
                  {p.payment_status === 'pending' ? (
                    <button onClick={() => onConfirm(p.id, p.transfer_code)} disabled={confirming === p.id}
                      className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold disabled:opacity-50 transition-all">
                      {confirming === p.id ? 'Dang...' : 'Duyet'}
                    </button>
                  ) : p.payment_reference ? (
                    <span className="text-[10px] text-slate-400 font-mono">{p.payment_reference}</span>
                  ) : '—'}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400 italic">Khong co don hang</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==========================================
// USERS TAB
// ==========================================
function UsersTab({
  users,
  actionLoading,
  onCreateUsers,
  onDeleteUser,
}: {
  users: UserBalance[];
  actionLoading: boolean;
  onCreateUsers: (payload: any) => Promise<any>;
  onDeleteUser: (userId: string, email?: string) => Promise<void>;
}) {
  const [sort, setSort] = useState<'credits' | 'used' | 'updated'>('updated');
  const [manual, setManual] = useState({ email: '', password: '', display_name: '', role: 'internal', organization: 'VDSC' });
  const [batchText, setBatchText] = useState('');
  const [batchResult, setBatchResult] = useState<any | null>(null);
  const sorted = [...users].sort((a, b) => {
    if (sort === 'credits') return Number(b.credits) - Number(a.credits);
    if (sort === 'used') return Number(b.credits_used) - Number(a.credits_used);
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  const parseBatch = () => {
    const rows = batchText
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(line => line && !line.startsWith('#'))
      .map(line => {
        const [email, password, displayName, role, organization] = line.split(',').map(x => x?.trim() || '');
        return {
          email,
          password,
          display_name: displayName || email?.split('@')[0],
          role: role || 'internal',
          organization: organization || 'VDSC',
        };
      });
    return rows;
  };

  const submitManual = async () => {
    const result = await onCreateUsers(manual);
    setBatchResult(result);
    if ((result?.failed || 0) === 0) {
      setManual({ email: '', password: '', display_name: '', role: 'internal', organization: 'VDSC' });
    }
  };

  const submitBatch = async () => {
    const rows = parseBatch();
    const result = await onCreateUsers({ users: rows });
    setBatchResult(result);
    if ((result?.failed || 0) === 0) setBatchText('');
  };

  const batchRows = parseBatch();

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1 bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="w-4 h-4 text-indigo-600" />
            <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-500">Add Manual</h3>
          </div>
          <div className="space-y-2">
            <input value={manual.email} onChange={e => setManual({ ...manual, email: e.target.value })}
              placeholder="email@vdsc.com.vn"
              className="w-full px-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-indigo-400" />
            <input value={manual.password} onChange={e => setManual({ ...manual, password: e.target.value })}
              placeholder="Mat khau tam thoi"
              type="text"
              className="w-full px-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-indigo-400" />
            <input value={manual.display_name} onChange={e => setManual({ ...manual, display_name: e.target.value })}
              placeholder="Ten hien thi"
              className="w-full px-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-indigo-400" />
            <div className="grid grid-cols-2 gap-2">
              <select value={manual.role} onChange={e => setManual({ ...manual, role: e.target.value })}
                className="px-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-indigo-400">
                <option value="internal">internal</option>
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
              <input value={manual.organization} onChange={e => setManual({ ...manual, organization: e.target.value })}
                placeholder="VDSC"
                className="px-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-indigo-400" />
            </div>
            <button onClick={submitManual} disabled={actionLoading || !manual.email || !manual.password}
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-bold disabled:opacity-50 transition-all flex items-center justify-center gap-2">
              <UserPlus className="w-3.5 h-3.5" />
              {actionLoading ? 'Dang tao...' : 'Tao user'}
            </button>
          </div>
        </div>

        <div className="col-span-2 bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-500">Batch Add</h3>
            </div>
            <span className="text-[10px] text-slate-400 font-mono">{batchRows.length}/50 rows</span>
          </div>
          <textarea
            value={batchText}
            onChange={e => setBatchText(e.target.value)}
            placeholder={"email,password,display_name,role,organization\nnam.ph@vdsc.com.vn,0934947729,Nam PH,internal,VDSC"}
            className="w-full h-28 px-3 py-2 border border-slate-200 rounded text-xs font-mono focus:outline-none focus:border-indigo-400 resize-none"
          />
          <div className="flex items-center gap-3 mt-2">
            <button onClick={submitBatch} disabled={actionLoading || batchRows.length === 0 || batchRows.length > 50}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded text-xs font-bold disabled:opacity-50 transition-all">
              {actionLoading ? 'Dang tao batch...' : 'Tao batch'}
            </button>
            <p className="text-[11px] text-slate-500">Format: email,password,display_name,role,organization. Role mac dinh internal.</p>
          </div>
          {batchResult?.results && (
            <div className="mt-3 max-h-24 overflow-y-auto border border-slate-100 rounded bg-slate-50 p-2 text-[11px] font-mono">
              {batchResult.results.map((r: any) => (
                <div key={r.email} className={r.ok ? 'text-emerald-700' : 'text-red-600'}>
                  {r.ok ? 'OK' : 'ERR'} {r.email}{r.error ? ` — ${r.error}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        {([['updated', 'Gan Day'], ['credits', 'Balance Cao'], ['used', 'Dung Nhieu']] as [string, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setSort(id as any)}
            className={`px-3 py-1 rounded text-[10px] uppercase font-bold transition-all
              ${sort === id ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left p-3 text-[10px] uppercase font-bold text-slate-400">User</th>
              <th className="text-center p-3 text-[10px] uppercase font-bold text-slate-400">Phone</th>
              <th className="text-right p-3 text-[10px] uppercase font-bold text-slate-400">Balance</th>
              <th className="text-right p-3 text-[10px] uppercase font-bold text-slate-400">Da Dung</th>
              <th className="text-right p-3 text-[10px] uppercase font-bold text-slate-400">Da Mua</th>
              <th className="text-center p-3 text-[10px] uppercase font-bold text-slate-400">Role</th>
              <th className="text-left p-3 text-[10px] uppercase font-bold text-slate-400">User ID</th>
              <th className="text-center p-3 text-[10px] uppercase font-bold text-slate-400">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map(u => (
              <tr key={u.user_id} className={`hover:bg-slate-50 ${u.deleted_at ? 'opacity-55 bg-slate-50' : ''}`}>
                <td className="p-3">
                  <div className="font-medium">{u.profiles?.display_name || '—'}</div>
                  <div className="text-[10px] text-slate-400">{u.profiles?.email || u.email || ''}</div>
                  {u.deleted_at && <div className="text-[10px] text-red-500 font-bold mt-0.5">soft-deleted</div>}
                </td>
                <td className="p-3 text-center">
                  {u.profiles?.phone_verified ? (
                    <span className="text-emerald-600 text-[10px] font-bold">Verified</span>
                  ) : (
                    <span className="text-slate-400 text-[10px]">—</span>
                  )}
                </td>
                <td className="p-3 text-right font-mono font-bold text-indigo-700">{Number(u.credits).toLocaleString()}</td>
                <td className="p-3 text-right font-mono text-red-600">{Number(u.credits_used).toLocaleString()}</td>
                <td className="p-3 text-right font-mono text-emerald-600">{Number(u.credits_purchased).toLocaleString()}</td>
                <td className="p-3 text-center">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase
                    ${u.profiles?.role === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500'}`}>
                    {u.profiles?.role || 'user'}
                  </span>
                </td>
                <td className="p-3 font-mono text-[10px] text-slate-400">{u.user_id.slice(0, 12)}...</td>
                <td className="p-3 text-center">
                  <button
                    onClick={() => onDeleteUser(u.user_id, u.profiles?.email || u.email)}
                    disabled={actionLoading || !!u.deleted_at || u.profiles?.role === 'admin'}
                    title={u.profiles?.role === 'admin' ? 'Ha role admin truoc khi xoa' : 'Soft-delete user'}
                    className="inline-flex items-center justify-center w-7 h-7 rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-white transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400 italic">Khong co user</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==========================================
// TRANSACTIONS TAB
// ==========================================
function TransactionsTab({ transactions }: { transactions: CreditTransaction[] }) {
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const filtered = typeFilter === 'all' ? transactions : transactions.filter(t => t.type === typeFilter);

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {['all', 'purchase', 'consume', 'welcome', 'bonus', 'refund'].map(f => (
          <button key={f} onClick={() => setTypeFilter(f)}
            className={`px-3 py-1 rounded text-[10px] uppercase font-bold transition-all
              ${typeFilter === f ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}>
            {f} {f !== 'all' && `(${transactions.filter(t => t.type === f).length})`}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left p-3 text-[10px] uppercase font-bold text-slate-400">Thoi Gian</th>
              <th className="text-center p-3 text-[10px] uppercase font-bold text-slate-400">Loai</th>
              <th className="text-right p-3 text-[10px] uppercase font-bold text-slate-400">Credits</th>
              <th className="text-right p-3 text-[10px] uppercase font-bold text-slate-400">Balance Sau</th>
              <th className="text-left p-3 text-[10px] uppercase font-bold text-slate-400">Ly Do</th>
              <th className="text-left p-3 text-[10px] uppercase font-bold text-slate-400">User ID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map(t => (
              <tr key={t.id} className="hover:bg-slate-50">
                <td className="p-3 font-mono text-slate-500">{new Date(t.created_at).toLocaleString('vi-VN')}</td>
                <td className="p-3 text-center"><TypeBadge type={t.type} /></td>
                <td className={`p-3 text-right font-mono font-bold ${t.credits >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {t.credits >= 0 ? '+' : ''}{t.credits.toLocaleString()}
                </td>
                <td className="p-3 text-right font-mono">{t.balance_after.toLocaleString()}</td>
                <td className="p-3 text-slate-600 max-w-[200px] truncate" title={t.reason || ''}>{t.reason || '—'}</td>
                <td className="p-3 font-mono text-[10px] text-slate-400">{t.user_id.slice(0, 12)}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400 italic">Khong co giao dich</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==========================================
// SHARED COMPONENTS
// ==========================================
function KpiCard({ icon: Icon, label, value, sub, color = 'text-slate-800', bg = 'bg-white border-slate-200' }: any) {
  return (
    <div className={`rounded-lg border p-4 ${bg}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-[9px] uppercase font-bold text-slate-400 tracking-widest">{label}</span>
      </div>
      <div className={`text-2xl font-mono font-black ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700',
    paid: 'bg-emerald-100 text-emerald-700',
    failed: 'bg-red-100 text-red-700',
    refunded: 'bg-slate-100 text-slate-600',
  };
  return <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${styles[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>;
}

function MethodBadge({ method }: { method: string }) {
  const styles: Record<string, string> = {
    momo: 'bg-pink-100 text-pink-700',
    vnpay: 'bg-blue-100 text-blue-700',
    bank: 'bg-slate-100 text-slate-600',
    zalopay: 'bg-sky-100 text-sky-700',
  };
  return <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${styles[method] || 'bg-slate-100 text-slate-600'}`}>{method}</span>;
}

function TypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    purchase: 'bg-emerald-100 text-emerald-700',
    consume: 'bg-red-100 text-red-700',
    welcome: 'bg-blue-100 text-blue-700',
    bonus: 'bg-violet-100 text-violet-700',
    refund: 'bg-amber-100 text-amber-700',
  };
  return <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${styles[type] || 'bg-slate-100 text-slate-600'}`}>{type}</span>;
}

// ======================================================================
// ExceptionsTab — list payment_exceptions với filter status
// ======================================================================
const EXCEPTION_TYPE_LABEL: Record<PaymentException['exception_type'], { label: string; color: string }> = {
  amount_overpaid:       { label: 'Chuyển thừa',     color: 'bg-amber-100 text-amber-700' },
  amount_too_small:      { label: 'Chuyển < 10K',    color: 'bg-rose-100 text-rose-700' },
  unknown_dtl:           { label: 'Không có DTL',    color: 'bg-orange-100 text-orange-700' },
  expired_order_paid:    { label: 'Đơn hết hạn (revive fail)', color: 'bg-rose-100 text-rose-700' },
  cancelled_order_paid:  { label: 'Đơn đã huỷ (revive fail)',  color: 'bg-rose-100 text-rose-700' },
  parse_error:           { label: 'Parse fail',      color: 'bg-red-100 text-red-700' },
  duplicate_confirmed:   { label: 'Duplicate (auto)',color: 'bg-slate-100 text-slate-700' },
};

type ResolutionAction =
  | 'credits_added_manual'
  | 'contact_user'
  | 'ignore'
  | 'bank_error'
  | 'refund_pending'
  | 'user_contacted'
  | 'manual_credit_after_verification'
  | 'auto_ignored';

interface ActionConfig {
  action: ResolutionAction;
  label: string;
  variant: 'primary' | 'secondary' | 'danger' | 'ghost';
  icon?: React.ComponentType<any>;
  requiresNote?: boolean;
  requiresEvidence?: boolean;
  requiresCreditsAmount?: boolean;
}

function getActionsForException(type: PaymentException['exception_type']): ActionConfig[] {
  switch (type) {
    case 'amount_overpaid':
      return [
        { action: 'refund_pending', label: 'Ghi nhận refund 3-7 business days', variant: 'primary', requiresNote: true },
        { action: 'user_contacted', label: 'Đã liên hệ user', variant: 'secondary', requiresNote: true },
        { action: 'ignore', label: 'Bỏ qua (cần lý do)', variant: 'ghost', requiresNote: true },
      ];
    case 'amount_too_small':
      return [
        { action: 'refund_pending', label: 'Ghi nhận refund thủ công', variant: 'primary', requiresNote: true },
        { action: 'user_contacted', label: 'Đã liên hệ user', variant: 'secondary', requiresNote: true },
        { action: 'ignore', label: 'Bỏ qua (tiền lạ, không phải user)', variant: 'ghost', requiresNote: true },
      ];
    case 'unknown_dtl':
      return [
        { action: 'manual_credit_after_verification', label: 'Cộng credits (cần evidence)', variant: 'primary', requiresNote: true, requiresEvidence: true, requiresCreditsAmount: true },
        { action: 'user_contacted', label: 'Đã liên hệ user xác nhận', variant: 'secondary', requiresNote: true },
        { action: 'ignore', label: 'Bỏ qua (tiền lạ)', variant: 'ghost', requiresNote: true },
      ];
    case 'expired_order_paid':
    case 'cancelled_order_paid':
      return [
        { action: 'credits_added_manual', label: 'Revive + cộng credits manual', variant: 'primary', requiresCreditsAmount: true },
        { action: 'ignore', label: 'Bỏ qua', variant: 'ghost', requiresNote: true },
      ];
    case 'parse_error':
      return [
        { action: 'bank_error', label: 'Escalate dev (bank đổi template)', variant: 'danger', requiresNote: true },
        { action: 'manual_credit_after_verification', label: 'Manual parse + cộng credits', variant: 'primary', requiresNote: true, requiresCreditsAmount: true },
        { action: 'ignore', label: 'Bỏ qua', variant: 'ghost', requiresNote: true },
      ];
    case 'duplicate_confirmed':
      // Auto-handled by Worker — display only
      return [];
    default:
      return [];
  }
}

function getScenarioText(ex: PaymentException): string {
  switch (ex.exception_type) {
    case 'amount_overpaid':
      const diff = (ex.parsed_amount || 0) - (ex.purchase?.price_vnd || 0);
      return `User chuyển ${ex.parsed_amount?.toLocaleString()}đ cho đơn ${ex.purchase?.price_vnd.toLocaleString()}đ (thừa ${diff.toLocaleString()}đ). Worker KHÔNG tự cộng credits. Đã notify user chọn refund hoặc bonus qua CS.`;
    case 'amount_too_small':
      return `User chuyển ${ex.parsed_amount?.toLocaleString()}đ, dưới ngưỡng xử lý tự động 10,000đ. Worker không cộng credits. Đã notify user liên hệ support.`;
    case 'unknown_dtl':
      return `Worker nhận ${ex.parsed_amount?.toLocaleString()}đ nhưng không match DTL nào (hoặc thiếu DTL). CHỈ cộng credits khi có evidence user đã liên hệ CS xác nhận.`;
    case 'expired_order_paid':
      return `DTL match đơn đã expired (>15 phút). Worker thử auto-revive nhưng thất bại. Admin có thể revive thủ công.`;
    case 'cancelled_order_paid':
      return `DTL match đơn đã cancelled. Worker thử auto-revive nhưng thất bại. Admin có thể revive thủ công.`;
    case 'parse_error':
      return `Regex không parse được body. Có thể bank đổi template email. Admin đọc raw body để extract manual.`;
    case 'duplicate_confirmed':
      return `DTL match đơn đã paid. Worker đã auto-create order mới cho user + cộng credits. Log audit, không cần action.`;
    default:
      return '';
  }
}

function ExceptionsTab({ exceptions, filter, onFilterChange, viewedIds, onSelect }: {
  exceptions: PaymentException[];
  filter: 'unresolved' | 'resolved' | 'ignored';
  onFilterChange: (f: 'unresolved' | 'resolved' | 'ignored') => void;
  viewedIds: Set<string>;
  onSelect: (ex: PaymentException) => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Filter bar */}
      <div className="p-4 border-b border-slate-100 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <h3 className="font-bold text-sm">Xử lý ngoại lệ</h3>
        <div className="ml-auto flex gap-1">
          {(['unresolved', 'resolved', 'ignored'] as const).map(f => (
            <button key={f} onClick={() => onFilterChange(f)}
              className={`px-3 py-1 rounded-lg text-xs font-bold capitalize transition-all
                ${filter === f ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {f === 'unresolved' ? 'Chưa xử lý' : f === 'resolved' ? 'Đã xử lý' : 'Bỏ qua'}
            </button>
          ))}
        </div>
      </div>

      {exceptions.length === 0 ? (
        <div className="p-12 text-center text-sm text-slate-400">Không có ngoại lệ nào</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Loại</th>
              <th className="px-4 py-2 text-left font-semibold">Mã CK</th>
              <th className="px-4 py-2 text-right font-semibold">Tiền</th>
              <th className="px-4 py-2 text-right font-semibold">Đơn match</th>
              <th className="px-4 py-2 text-left font-semibold">Thời gian</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {exceptions.map(ex => {
              const typeInfo = EXCEPTION_TYPE_LABEL[ex.exception_type] || { label: ex.exception_type, color: 'bg-slate-100' };
              const isUnviewed = ex.status === 'unresolved' && !viewedIds.has(ex.id);
              return (
                <tr key={ex.id} className={`border-t border-slate-100 hover:bg-slate-50 ${isUnviewed ? 'bg-amber-50/40 font-semibold' : ''}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {isUnviewed && <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" title="Chưa xem" />}
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${typeInfo.color}`}>{typeInfo.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2 font-mono text-[11px]">{ex.parsed_transfer_code || '—'}</td>
                  <td className="px-4 py-2 text-right font-mono">{ex.parsed_amount ? ex.parsed_amount.toLocaleString() + 'đ' : '—'}</td>
                  <td className="px-4 py-2 text-right text-[11px]">{ex.purchase ? `${ex.purchase.price_vnd.toLocaleString()}đ` : '—'}</td>
                  <td className="px-4 py-2 text-slate-500 text-[11px]">{new Date(ex.created_at).toLocaleString('vi-VN')}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => onSelect(ex)}
                      className="px-3 py-1 rounded-lg text-[11px] font-bold bg-indigo-600 text-white hover:bg-indigo-700">
                      {isUnviewed ? 'Xem' : 'Xử lý'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ======================================================================
// MailLogTab — list bank_emails
// ======================================================================
const EMAIL_STATUS_COLOR: Record<BankEmailRow['processing_status'], string> = {
  received: 'bg-slate-100 text-slate-600',
  confirmed: 'bg-emerald-100 text-emerald-700',
  exception: 'bg-amber-100 text-amber-700',
  rejected_sender: 'bg-red-100 text-red-700',
  ignored_outgoing: 'bg-blue-100 text-blue-700',
};

function MailLogTab({ emails }: { emails: BankEmailRow[] }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex items-center gap-2">
        <Mail className="w-4 h-4 text-indigo-600" />
        <h3 className="font-bold text-sm">Mail Log — email bank nhận được</h3>
        <span className="ml-auto text-xs text-slate-400">{emails.length} email</span>
      </div>
      {emails.length === 0 ? (
        <div className="p-12 text-center text-sm text-slate-400">Chưa có email nào</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Thời gian</th>
              <th className="px-4 py-2 text-left font-semibold">Từ</th>
              <th className="px-4 py-2 text-left font-semibold">Subject</th>
              <th className="px-4 py-2 text-right font-semibold">Tiền</th>
              <th className="px-4 py-2 text-left font-semibold">Mã CK</th>
              <th className="px-4 py-2 text-left font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {emails.map(e => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 text-slate-500 text-[11px]">{new Date(e.received_at).toLocaleString('vi-VN')}</td>
                <td className="px-4 py-2 text-[11px]">{e.email_from}</td>
                <td className="px-4 py-2 truncate max-w-[200px]">{e.email_subject}</td>
                <td className="px-4 py-2 text-right font-mono">{e.parsed_amount ? e.parsed_amount.toLocaleString() : '—'}</td>
                <td className="px-4 py-2 font-mono text-[11px]">{e.parsed_transfer_code || '—'}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${EMAIL_STATUS_COLOR[e.processing_status] || 'bg-slate-100'}`}>
                    {e.processing_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ======================================================================
// ExceptionDrawer — chi tiết + 3 action buttons
// ======================================================================
interface MatchingPurchase {
  id: string;
  transfer_code: string;
  payment_status: string;
  credits_total: number;
  created_at: string;
  user_id: string;
  profiles?: { display_name?: string; role?: string };
}

function ExceptionDrawer({ exception, note, setNote, creditsAmount, setCreditsAmount, resolving, onResolve, onClose, accessToken }: {
  exception: PaymentException;
  note: string; setNote: (v: string) => void;
  creditsAmount: string; setCreditsAmount: (v: string) => void;
  resolving: boolean;
  onResolve: (action: ResolutionAction, purchaseIdOverride?: string) => void;
  onClose: () => void;
  accessToken?: string;
}) {
  const typeInfo = EXCEPTION_TYPE_LABEL[exception.exception_type] || { label: exception.exception_type, color: 'bg-slate-100' };
  const [matchingPurchases, setMatchingPurchases] = React.useState<MatchingPurchase[]>([]);
  const [selectedPurchaseId, setSelectedPurchaseId] = React.useState<string | null>(exception.matched_purchase_id);

  // Rule 3: fetch matching purchases khi exception_type = unknown_dtl
  React.useEffect(() => {
    if (exception.exception_type !== 'unknown_dtl' || !accessToken) return;
    fetch(`${WORKERS_URL}/api/admin/exceptions/${exception.id}/matching-purchases`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.ok ? r.json() : { purchases: [] })
      .then(({ purchases }) => setMatchingPurchases(purchases || []))
      .catch(() => setMatchingPurchases([]));
  }, [exception.id, exception.exception_type, accessToken]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black/30" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <div>
            <h3 className="font-bold text-slate-800">Xử lý ngoại lệ</h3>
            <span className={`inline-block mt-0.5 px-2 py-0.5 rounded text-[10px] font-bold ${typeInfo.color}`}>{typeInfo.label}</span>
          </div>
          <button onClick={onClose} className="ml-auto p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Scenario description (codex D spec) */}
          <section>
            <h4 className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-2">Tình huống</h4>
            <div className="bg-slate-50 border-l-4 border-slate-300 rounded p-3 text-sm text-slate-700 leading-relaxed">
              {getScenarioText(exception)}
            </div>
          </section>

          {/* Parsed */}
          <section>
            <h4 className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-2">Parse</h4>
            <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
              <div><span className="text-slate-500">Số tiền nhận:</span> <strong>{exception.parsed_amount?.toLocaleString() || '—'}đ</strong></div>
              <div><span className="text-slate-500">Mã CK:</span> <span className="font-mono">{exception.parsed_transfer_code || '—'}</span></div>
            </div>
          </section>

          {/* Rule 3: Suggest users cùng amount (chỉ cho unknown_dtl) */}
          {exception.exception_type === 'unknown_dtl' && matchingPurchases.length > 0 && (
            <section>
              <h4 className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-2">
                🔍 Users có order cùng amount {exception.parsed_amount?.toLocaleString()}đ (24h qua)
              </h4>
              <div className="space-y-2">
                {matchingPurchases.map(p => (
                  <label key={p.id} className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all
                    ${selectedPurchaseId === p.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                    <input type="radio" checked={selectedPurchaseId === p.id} onChange={() => setSelectedPurchaseId(p.id)}
                      className="w-4 h-4 text-indigo-600" />
                    <div className="flex-1 text-xs">
                      <div className="font-bold">{p.profiles?.display_name || 'Unknown user'}</div>
                      <div className="font-mono text-slate-500 text-[10px]">{p.transfer_code}</div>
                    </div>
                    <div className="text-right text-[11px]">
                      <span className={`px-2 py-0.5 rounded font-bold ${
                        p.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        p.payment_status === 'expired' ? 'bg-rose-100 text-rose-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{p.payment_status}</span>
                      <div className="text-slate-400 mt-1">{new Date(p.created_at).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Chọn user match → bấm "Cộng credits manual" sẽ cộng cho user này.
              </p>
            </section>
          )}

          {/* Matched purchase */}
          {exception.purchase && (
            <section>
              <h4 className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-2">Đơn khớp</h4>
              <div className="bg-indigo-50 rounded-lg p-3 space-y-1 text-sm">
                <div><span className="text-slate-500">Giá trị:</span> <strong>{exception.purchase.price_vnd.toLocaleString()}đ</strong> · {exception.purchase.credits_total.toLocaleString()} credits</div>
                <div><span className="text-slate-500">Status:</span> <span className="font-mono text-xs">{exception.purchase.payment_status}</span></div>
                {exception.parsed_amount && exception.parsed_amount !== exception.purchase.price_vnd && (
                  <div className="text-rose-600 font-bold text-xs mt-2">
                    Chênh lệch: {(exception.parsed_amount - exception.purchase.price_vnd).toLocaleString()}đ
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Email */}
          {exception.bank_email && (
            <section>
              <h4 className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-2">Email</h4>
              <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                <div className="text-slate-500">From: {exception.bank_email.email_from}</div>
                <div className="font-medium">{exception.bank_email.email_subject}</div>
                <details className="mt-2">
                  <summary className="cursor-pointer text-indigo-600 text-[11px]">Xem body đầy đủ</summary>
                  <pre className="mt-2 p-2 bg-white border border-slate-200 rounded text-[10px] whitespace-pre-wrap max-h-60 overflow-y-auto">
                    {exception.bank_email.email_body?.slice(0, 3000) || '(trống)'}
                  </pre>
                </details>
              </div>
            </section>
          )}

          {/* Resolve form */}
          {exception.status === 'unresolved' && (
            <section>
              <h4 className="text-[10px] uppercase font-bold tracking-widest text-slate-400 mb-2">Quyết định</h4>

              {(() => {
                const actions = getActionsForException(exception.exception_type);
                if (actions.length === 0) {
                  return (
                    <div className="bg-slate-100 rounded-lg p-4 text-sm text-slate-600">
                      ℹ️ Exception này auto-handled bởi Worker, không cần admin action (display only cho audit).
                    </div>
                  );
                }
                // Any action needs credits amount input?
                const needsCredits = actions.some(a => a.requiresCreditsAmount);
                const needsEvidence = actions.some(a => a.requiresEvidence);

                return (
                  <>
                    {needsCredits && (
                      <label className="block mb-3">
                        <span className="text-xs text-slate-600">Credits cộng (nếu chọn action cộng)</span>
                        <input type="number" value={creditsAmount} onChange={e => setCreditsAmount(e.target.value)}
                          placeholder={exception.purchase?.credits_total?.toString() || 'VD: 100000'}
                          className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                      </label>
                    )}

                    <label className="block mb-3">
                      <span className="text-xs text-slate-600">
                        Ghi chú xử lý
                        {needsEvidence && <span className="ml-1 text-rose-600 font-bold">⚠ BẮT BUỘC ghi evidence (user đã liên hệ CS, thông tin xác nhận)</span>}
                      </span>
                      <textarea value={note} onChange={e => setNote(e.target.value)}
                        placeholder={needsEvidence ? 'VD: User A đã email CS 15:30, xác nhận đúng order này...' : 'VD: Đã gửi email user, chờ phản hồi...'}
                        className={`mt-1 w-full px-3 py-2 border rounded-lg text-sm h-20 ${needsEvidence ? 'border-rose-300 bg-rose-50' : 'border-slate-300'}`} />
                    </label>

                    <div className="grid grid-cols-1 gap-2">
                      {actions.map(({ action, label, variant, requiresNote, requiresEvidence, requiresCreditsAmount }) => {
                        const disabled = resolving
                          || (requiresNote && !note)
                          || (requiresEvidence && note.length < 20) // basic evidence length check
                          || (requiresCreditsAmount && !creditsAmount)
                          || (action === 'manual_credit_after_verification' && !exception.purchase && !selectedPurchaseId);
                        const classMap: Record<typeof variant, string> = {
                          primary: 'bg-emerald-600 text-white hover:bg-emerald-700',
                          secondary: 'bg-slate-600 text-white hover:bg-slate-700',
                          danger: 'bg-rose-600 text-white hover:bg-rose-700',
                          ghost: 'border-2 border-slate-300 text-slate-700 hover:bg-slate-50',
                        };
                        return (
                          <button
                            key={action}
                            disabled={disabled}
                            onClick={() => onResolve(action, selectedPurchaseId && selectedPurchaseId !== exception.matched_purchase_id ? selectedPurchaseId : undefined)}
                            className={`px-4 py-2.5 rounded-lg text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2 ${classMap[variant]}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </section>
          )}

          {exception.status !== 'unresolved' && (
            <section>
              <div className="bg-slate-100 rounded-lg p-4 text-sm">
                <div><strong>Đã xử lý:</strong> {exception.resolution_action}</div>
                {exception.resolution_note && <div className="mt-1 text-slate-600">{exception.resolution_note}</div>}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default CommercialAdmin;
